import { CartLinesUpdateEvent, CartDiscountUpdateEvent } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';

const isReward = item => ['club-cap', 'gift'].includes(item.properties?._mis_reward);
const root = () => window.Shopify?.routes?.root || '/';
let busy = false;
let timer;
const widget = () => document.querySelector('mis-cart-rewards');
const sections = () => [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
async function jsonRequest(path, body) {
  const response = await fetch(root() + path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : { cache: 'no-store' })
  });
  const data = await response.json();
  if (!response.ok || data.errors) throw new Error(data.description || 'Cart update failed');
  return data;
}
function notify(message) {
  document.querySelectorAll('mis-cart-rewards .mis-rewards__error').forEach(el => {
    el.textContent = message;
    el.hidden = false;
  });
}
function amount(cart, product) {
  return Math.max(0, cart.total_price - cart.items.filter(i => String(i.product_id) === product).reduce((sum, i) => sum + i.final_line_price, 0));
}
function emit(promise, action) {
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [], promise }));
}
function result(cart, rendered) {
  return { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: rendered, items: cart.items, itemCount: cart.item_count, source: 'mis-cart-rewards', didError: false } };
}
async function change(updates) {
  return jsonRequest('cart/update.js', { updates, sections: sections(), sections_url: location.pathname });
}
async function clean() {
  const el = widget();
  if (!el || busy) return;
  await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
  if (busy) return;
  busy = true;
  try {
    const cart = await jsonRequest('cart.js');
    const marked = cart.items.filter(isReward);
    if (!marked.length) return;
    const updates = {};
    let retained = false;
    for (const item of marked) {
      if (String(item.product_id) !== el.dataset.product || cart.currency !== 'EUR' || amount(cart, el.dataset.product) < 19900 || item.final_line_price > 0 || retained) updates[item.key] = 0;
      else { retained = true; if (item.quantity !== 1) updates[item.key] = 1; }
    }
    if (Object.keys(updates).length) {
      const promise = change(updates).then(c => result(c, c.sections));
      emit(promise, 'remove');
      await promise;
      setTimeout(() => notify(el.dataset.removed), 250);
    }
  } catch {
    notify(el.dataset.error);
  } finally { busy = false; }
}
function schedule(event) {
  if (busy) return;
  Promise.resolve(event?.promise).catch(() => {}).finally(() => {
    clearTimeout(timer);
    timer = setTimeout(clean, 180);
  });
}
document.addEventListener(CartLinesUpdateEvent.eventName || 'shopify:cart:lines-update', schedule);
document.addEventListener(CartDiscountUpdateEvent.eventName || 'shopify:cart:discount-update', schedule);
window.addEventListener('pageshow', schedule);

async function add(button, el) {
  if (busy) return;
  busy = true;
  button.disabled = true;
  const label = button.textContent;
  button.textContent = el.dataset.pending;
  try {
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    const before = await jsonRequest('cart.js');
    if (before.currency !== 'EUR' || amount(before, el.dataset.product) < 19900 || before.items.some(i => String(i.product_id) === el.dataset.product)) throw new Error('Not eligible');
    const promise = (async () => {
      const added = await jsonRequest('cart/add.js', { items: [{ id: Number(el.dataset.variant), quantity: 1, properties: { _mis_reward: 'gift' } }], sections: sections(), sections_url: location.pathname });
      const cart = await jsonRequest('cart.js');
      const cap = cart.items.find(i => String(i.product_id) === el.dataset.product && isReward(i));
      if (!cap || cap.final_line_price !== 0 || amount(cart, el.dataset.product) < 19900) {
        if (cap) {
          const restored = await change({ [cap.key]: 0 });
          setTimeout(() => notify(el.dataset.error), 350);
          return result(restored, restored.sections);
        }
        throw new Error('Gift discount unavailable');
      }
      return result(cart, added.sections);
    })();
    emit(promise, 'add');
    await promise;
  } catch {
    notify(el.dataset.error);
  } finally {
    busy = false;
    button.disabled = false;
    button.textContent = label;
  }
}
document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const zoom = event.target.closest('[data-mis-zoom]');
  if (zoom) {
    event.preventDefault();
    const el = zoom.closest('mis-cart-rewards');
    const dialog = document.createElement('dialog');
    dialog.className = 'mis-rewards-dialog';
    dialog.setAttribute('aria-label', zoom.getAttribute('aria-label'));
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = el.dataset.close;
    close.addEventListener('click', () => dialog.close());
    const image = document.createElement('img');
    image.src = zoom.href;
    image.alt = zoom.querySelector('img')?.alt || '';
    dialog.append(close, image);
    dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => { dialog.remove(); zoom.focus(); });
    document.body.append(dialog);
    dialog.showModal();
    return;
  }
  const button = event.target.closest('[data-mis-add]');
  if (button) { event.preventDefault(); add(button, button.closest('mis-cart-rewards')); }
});
let checkoutReleased = false;
document.addEventListener('submit', async event => {
  if (checkoutReleased || event.submitter?.name !== 'checkout' || !widget()) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const form = event.target;
  const submitter = event.submitter;
  try {
    for (let i = 0; busy && i < 100; i++) await new Promise(resolve => setTimeout(resolve, 50));
    if (busy) throw new Error('Cart busy');
    await clean();
    const cart = await jsonRequest('cart.js');
    const el = widget();
    if (cart.items.some(i => isReward(i) && (String(i.product_id) !== el?.dataset.product || i.final_line_price > 0 || amount(cart, el.dataset.product) < 19900))) throw new Error('Gift not eligible');
    checkoutReleased = true;
    const currentForm = document.getElementById(form.id) || form;
    const currentSubmitter = submitter?.isConnected ? submitter : document.querySelector('[name="checkout"]');
    currentForm.requestSubmit(currentSubmitter?.form === currentForm ? currentSubmitter : undefined);
  } catch { notify(widget()?.dataset.error || 'Bitte den Warenkorb erneut prüfen.'); }
}, true);
